import {
  faArrowProgress,
  faBriefcaseMedical,
  faChartLine,
  faClock,
  faCloudArrowUp,
  faFileChartColumn,
  faFileLines,
  faFolderArrowUp,
  faHouse,
  faList,
  faLocationDot,
  faLocationPin,
  faMagnifyingGlassPlus,
  faPrint,
  faSquare,
  faTags,
  faToolbox,
  faUpload,
  faUser,
  faUserDoctor,
  faUserGroup,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';

import { NavbarLinkItem } from './types/NavbarLinkItem';

export const NAVIGATION_LINK_IDS = {
  HOME: 1,
  EXAMS: 2,
  PATIENTS: 3,
  UPLOAD_EXAM: 4,
  ADMIN: 5,
  ACCOUNTS: 17,
  LOCATIONS: 6,
  LOCATION_TYPES: 7,
  DESTINATIONS: 14,
  DEVICES: 8,
  PHYSICIANS: 9,
  TEMPLATES: 15,
  USERS: 10,
  GROUPS: 11,
  UPLOAD_TRACKER: 12,
  SEND_QUEUE: 13,
  WORKLIST_VIEW_USER: 16,
  BULK_UPLOAD: 18,
  PORTAL_SETTINGS: 19,
  SOURCES: 20,
  PACS: 21,
  HL7SEND: 22,
  DESTINATION_ROUTE: 23,
  LOCATIONFINALREPORT_TEMPLATE: 24,
  FORM: 25,
  SERVICE: 26,
  QUERY_RETRIEVE: 27,
  TAGS: 28,
  WORKLIST_MANAGEMENT: 29,
  ADMIN_DASHBOARD: 30,
};

export const BASE_HOME_ITEM = {
  text: 'Home',
  icon: 'fa-house',
  defaultExternalRoute: '/', // Changed to React app root instead of legacy portal
};

// Separate constant for legacy portal URLs that navigation items still need
const LEGACY_PORTAL_BASE_URL = import.meta.env.VITE_APP_COMPUMED_PORTAL_BASE_URL;

export const NavigationItems = {
  SEPARATOR: {
    separator: true,
  },
  HOME: {
    text: BASE_HOME_ITEM.text,
    icon: BASE_HOME_ITEM.icon,
    id: NAVIGATION_LINK_IDS.HOME,
    externalRoute: BASE_HOME_ITEM.defaultExternalRoute,
  },
  PATIENT: {
    text: 'Patients',
    icon: 'fa-users',
    id: NAVIGATION_LINK_IDS.PATIENTS,
    route: '/patient-2/',
    externalRoute: `${LEGACY_PORTAL_BASE_URL}/Patient`,
  },
  EXAMS: {
    text: 'Exams',
    icon: 'fa-file-lines',
    id: NAVIGATION_LINK_IDS.EXAMS,
    route: '/exam/',
    externalRoute: `${LEGACY_PORTAL_BASE_URL}/Exams/Index2`,
  },
  UPLOAD_EXAM: {
    text: 'Upload Exam',
    icon: 'fa-upload',
    id: NAVIGATION_LINK_IDS.UPLOAD_EXAM,
    route: '/upload/',
    externalRoute: `${LEGACY_PORTAL_BASE_URL}/DICOM/UploadDICOM`,
  },
  QUERY_RETRIEVE: {
    text: 'Query Retrieve',
    icon: 'fa-magnifying-glass-plus',
    id: NAVIGATION_LINK_IDS.QUERY_RETRIEVE,
    route: '/query-retrieve/',
  },
  ADMIN: {
    text: 'Admin',
    icon: 'fa-toolbox',
    id: NAVIGATION_LINK_IDS.ADMIN,
    dataExpanded: false,
    allowedRoles: ['Administrator'],
  },
  ACCOUNTS: {
    text: 'Accounts',
    icon: 'fa-square',
    id: NAVIGATION_LINK_IDS.ACCOUNTS,
    parentId: 5,
    route: '/account/',
  },
  LOCATIONS: {
    text: 'Locations',
    icon: 'fa-location-pin',
    id: NAVIGATION_LINK_IDS.LOCATIONS,
    parentId: 5,
    route: '/location/',
  },
  LOCATION_TYPES: {
    text: 'Location Types',
    icon: 'fa-location-dot',
    id: NAVIGATION_LINK_IDS.LOCATION_TYPES,
    parentId: 5,
    route: '/location-type/',
  },
  DESTINATIONS: {
    text: 'Destinations',
    icon: 'fa-square',
    id: NAVIGATION_LINK_IDS.DESTINATIONS,
    parentId: 5,
    route: '/destination/',
  },
  SOURCES: {
    text: 'Sources',
    icon: 'fa-square',
    id: NAVIGATION_LINK_IDS.SOURCES,
    parentId: 5,
    route: '/source/',
  },
  DESTINATION_ROUTE: {
    text: 'Destination Routes',
    icon: 'fa-arrow-progress',
    id: NAVIGATION_LINK_IDS.DESTINATION_ROUTE,
    parentId: 5,
    route: '/destination-route/',
  },
  TAGS: {
    text: 'Tags',
    icon: 'fa-tags',
    id: NAVIGATION_LINK_IDS.TAGS,
    parentId: 5,
    route: '/tags/',
  },
  FORM: {
    text: 'Forms',
    icon: 'fa-file-lines',
    id: NAVIGATION_LINK_IDS.FORM,
    parentId: 5,
    route: '/form/',
  },
  LOCATIONFINALREPORT_TEMPLATE: {
    text: 'Location Final Report Template',
    icon: 'fa-file-chart-column',
    id: NAVIGATION_LINK_IDS.LOCATIONFINALREPORT_TEMPLATE,
    parentId: 5,
    route: '/location-finalreport-template/',
  },
  PACS: {
    text: 'PACS',
    icon: 'fa-square',
    id: NAVIGATION_LINK_IDS.PACS,
    parentId: 5,
    route: '/pacs/',
  },
  HL7SEND: {
    text: 'HL7Send',
    icon: 'fa-square',
    id: NAVIGATION_LINK_IDS.HL7SEND,
    parentId: 5,
    route: '/hl7send/',
  },
  SERVICE: {
    text: 'Services',
    icon: 'fa-solid fa-briefcase-medical',
    id: NAVIGATION_LINK_IDS.SERVICE,
    parentId: 5,
    route: '/service/',
  },
  DEVICES: {
    text: 'Devices',
    icon: 'fa-print',
    id: NAVIGATION_LINK_IDS.DEVICES,
    parentId: 5,
    route: '/unit/',
  },
  PHYSICIANS: {
    text: 'Physicians',
    icon: 'fa-user-doctor',
    id: NAVIGATION_LINK_IDS.PHYSICIANS,
    parentId: 5,
    route: '/physician/',
  },
  TEMPLATES: {
    text: 'Templates',
    icon: 'fa-square',
    id: NAVIGATION_LINK_IDS.TEMPLATES,
    parentId: 5,
    route: '/template/',
  },
  USERS: {
    text: 'Users',
    icon: 'fa-user',
    id: NAVIGATION_LINK_IDS.USERS,
    parentId: 5,
    route: '/user/',
  },
  GROUPS: {
    text: 'Groups',
    icon: 'fa-user-group',
    id: NAVIGATION_LINK_IDS.GROUPS,
    parentId: 5,
    route: '/group/',
  },
  UPLOAD_TRACKER: {
    text: 'Upload Tracker',
    icon: 'fa-cloud-arrow-up',
    id: NAVIGATION_LINK_IDS.UPLOAD_TRACKER,
    parentId: 5,
    route: '/upload-tracker/',
  },
  SEND_QUEUE: {
    text: 'Send Queue',
    icon: 'fa-clock',
    id: NAVIGATION_LINK_IDS.SEND_QUEUE,
    parentId: 5,
    route: '/send-queue/',
  },
  PORTAL_SETTINGS: {
    text: 'Portal Settings',
    icon: 'fa-toolbox',
    id: NAVIGATION_LINK_IDS.PORTAL_SETTINGS,
    parentId: 5,
    route: '/settings/',
  },
  WORKLIST_VIEW_USER: {
    text: 'Worklist Access',
    icon: 'fa-square',
    id: NAVIGATION_LINK_IDS.WORKLIST_VIEW_USER,
    parentId: 5,
    route: '/worklist-view-user/',
  },
  WORKLIST_MANAGEMENT: {
    text: 'Worklist Management',
    icon: 'fa-list',
    id: NAVIGATION_LINK_IDS.WORKLIST_MANAGEMENT,
    parentId: 5,
    route: '/worklist-management/',
  },
  ADMIN_DASHBOARD: {
    text: 'Dashboard',
    icon: 'fa-chart-line',
    id: NAVIGATION_LINK_IDS.ADMIN_DASHBOARD,
    parentId: 5,
    route: '/dashboard/',
  },
  BULK_UPLOAD: {
    icon: 'fa-folder-arrow-up',
    id: NAVIGATION_LINK_IDS.BULK_UPLOAD,
    externalRoute: `${LEGACY_PORTAL_BASE_URL}/Exams/BulkUpload`,
    text: 'Bulk Upload',
  },
};

export const HIDDEN_APPLICATION_SHELL_PATHS = [
  new RegExp('/exam/add', 'i'),
  new RegExp('\\/exam\\/+?\\d+\\/edit', 'i'),
  new RegExp('\\/exam\\/+?\\d+\\/read$', 'i'),
  new RegExp('/share', 'i'),
];

export const SHARE_BASE_ROUTE_ID = 'share_base_route';

export const NAVIGATION_SIDEBAR_LOCAL_STORAGE_KEY = 'navigation-sidebar';

/** Navigation bar preferred width in pixels for desktop.  This is not a guarantee, because the viewport width will cause the navbar to shrink. */
export const NAVBAR_DESKTOP_PREFERRED_WIDTH = 200;

/** Navigation bar preferred width in pixels for mobile.  This is not a guarantee, because the viewport width will cause the navbar to shrink. */
export const NAVBAR_MOBILE_PREFERRED_WIDTH = 300;

/** The width in pixels of the navbar in mini mode. */
export const NAVBAR_MINI_WIDTH = 40;

export const NAVBAR_LINKS: NavbarLinkItem[] = [
  {
    id: NAVIGATION_LINK_IDS.HOME,
    to: '/',
    title: 'Home',
    text: 'Home',
    icon: faHouse,
  },
  {
    id: NAVIGATION_LINK_IDS.PATIENTS,
    to: '/patient-2/',
    title: 'Patients',
    text: 'Patients',
    icon: faUsers,
  },
  {
    id: NAVIGATION_LINK_IDS.EXAMS,
    to: '/exam/',
    title: 'Exams',
    text: 'Exams',
    icon: faFileLines,
  },
  {
    id: NAVIGATION_LINK_IDS.UPLOAD_EXAM,
    to: '/upload/',
    title: 'Upload Exam',
    text: 'Upload Exam',
    icon: faUpload,
  },
  {
    id: NAVIGATION_LINK_IDS.QUERY_RETRIEVE,
    to: '/query-retrieve/',
    title: 'Query Retrieve',
    text: 'Query Retrieve',
    icon: faMagnifyingGlassPlus,
  },
  {
    id: NAVIGATION_LINK_IDS.ADMIN,
    title: 'Admin',
    text: 'Admin',
    icon: faToolbox,
    children: [
      {
        id: NAVIGATION_LINK_IDS.ACCOUNTS,
        to: '/account/',
        title: 'Accounts',
        text: 'Accounts',
        icon: faSquare,
      },
      {
        id: NAVIGATION_LINK_IDS.LOCATIONS,
        to: '/location/',
        title: 'Locations',
        text: 'Locations',
        icon: faLocationPin,
      },
      {
        id: NAVIGATION_LINK_IDS.LOCATION_TYPES,
        to: '/location-type/',
        title: 'Location Types',
        text: 'Location Types',
        icon: faLocationDot,
      },
      {
        id: NAVIGATION_LINK_IDS.DESTINATIONS,
        to: '/destination/',
        title: 'Destinations',
        text: 'Destinations',
        icon: faSquare,
      },
      {
        id: NAVIGATION_LINK_IDS.SOURCES,
        to: '/source/',
        title: 'Sources',
        text: 'Sources',
        icon: faSquare,
      },
      {
        id: NAVIGATION_LINK_IDS.DESTINATION_ROUTE,
        to: '/destination-route/',
        title: 'Destination Routes',
        text: 'Destination Routes',
        icon: faArrowProgress,
      },
      {
        id: NAVIGATION_LINK_IDS.TAGS,
        to: '/tags/',
        title: 'Tags',
        text: 'Tags',
        icon: faTags,
      },
      {
        id: NAVIGATION_LINK_IDS.FORM,
        to: '/form/',
        title: 'Forms',
        text: 'Forms',
        icon: faFileLines,
      },
      {
        id: NAVIGATION_LINK_IDS.LOCATIONFINALREPORT_TEMPLATE,
        to: '/location-finalreport-template/',
        title: 'Location Final Report Template',
        text: 'Location Final Report Template',
        icon: faFileChartColumn,
      },
      {
        id: NAVIGATION_LINK_IDS.PACS,
        to: '/pacs/',
        title: 'PACS',
        text: 'PACS',
        icon: faSquare,
      },
      {
        id: NAVIGATION_LINK_IDS.HL7SEND,
        to: '/hl7send/',
        title: 'HL7Send',
        text: 'HL7Send',
        icon: faSquare,
      },
      {
        id: NAVIGATION_LINK_IDS.SERVICE,
        to: '/service/',
        title: 'Services',
        text: 'Services',
        icon: faBriefcaseMedical,
      },
      {
        id: NAVIGATION_LINK_IDS.DEVICES,
        to: '/unit/',
        title: 'Devices',
        text: 'Devices',
        icon: faPrint,
      },
      {
        id: NAVIGATION_LINK_IDS.PHYSICIANS,
        to: '/physician/',
        title: 'Physicians',
        text: 'Physicians',
        icon: faUserDoctor,
      },
      {
        id: NAVIGATION_LINK_IDS.TEMPLATES,
        to: '/template/',
        title: 'Templates',
        text: 'Templates',
        icon: faSquare,
      },
      {
        id: NAVIGATION_LINK_IDS.USERS,
        to: '/user/',
        title: 'Users',
        text: 'Users',
        icon: faUser,
      },
      {
        id: NAVIGATION_LINK_IDS.GROUPS,
        to: '/group/',
        title: 'Groups',
        text: 'Groups',
        icon: faUserGroup,
      },
      {
        id: NAVIGATION_LINK_IDS.UPLOAD_TRACKER,
        to: '/upload-tracker/',
        title: 'Upload Tracker',
        text: 'Upload Tracker',
        icon: faCloudArrowUp,
      },
      {
        id: NAVIGATION_LINK_IDS.SEND_QUEUE,
        to: '/send-queue/',
        title: 'Send Queue',
        text: 'Send Queue',
        icon: faClock,
      },
      {
        id: NAVIGATION_LINK_IDS.PORTAL_SETTINGS,
        to: '/settings/',
        title: 'Portal Settings',
        text: 'Portal Settings',
        icon: faToolbox,
      },
      {
        id: NAVIGATION_LINK_IDS.WORKLIST_VIEW_USER,
        to: '/worklist-view-user/',
        title: 'Worklist Access',
        text: 'Worklist Access',
        icon: faSquare,
      },
      {
        id: NAVIGATION_LINK_IDS.WORKLIST_MANAGEMENT,
        to: '/worklist-management/',
        title: 'Worklist Management',
        text: 'Worklist Management',
        icon: faList,
      },
      {
        id: NAVIGATION_LINK_IDS.ADMIN_DASHBOARD,
        to: '/dashboard/',
        title: 'Dashboard',
        text: 'Dashboard',
        icon: faChartLine,
      },
    ],
  },
  {
    id: NAVIGATION_LINK_IDS.BULK_UPLOAD,
    to: '/',
    title: 'Bulk Upload',
    text: 'Bulk Upload',
    icon: faFolderArrowUp,
  },
];
